import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { wheelApiService } from '../services/wheelApi';
import { promotionApiService, Promotion } from '../services/promotionApi';
import { serviceApiService, Service } from '../services/serviceApi';
import { useAuth } from './AuthProvider';
import { useIrisAvatar } from '../hooks/useIrisAvatar';

interface WheelSegment {
  id: string;
  label: string;
  color: string;
  rotation: number;
  promotion?: Promotion;
}

const PromoWheel: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { avatarUrl } = useIrisAvatar();
  const [segments, setSegments] = useState<WheelSegment[]>([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [wonPromotion, setWonPromotion] = useState<Promotion | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [canPlay, setCanPlay] = useState(true);
  const [services, setServices] = useState<Service[]>([]);
  const [userActivePromotions, setUserActivePromotions] = useState<Promotion[]>([]);
  const [canWin, setCanWin] = useState(true);
  const [daysUntilNextPlay, setDaysUntilNextPlay] = useState(0);
  const [daysUntilNextWin, setDaysUntilNextWin] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [replayDelay, setReplayDelay] = useState(7);
  const [winDelay, setWinDelay] = useState(1);
  const [showRules, setShowRules] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  const wheelRef = useRef<HTMLDivElement>(null);

  // Fonction pour formater le délai en format lisible
  const formatDelay = (days: number) => {
    const totalMinutes = Math.ceil(days * 24 * 60);
    const d = Math.floor(totalMinutes / (24 * 60));
    const h = Math.floor((totalMinutes % (24 * 60)) / 60);
    const m = totalMinutes % 60;
    
    if (d > 0) {
      return `${d} jour${d > 1 ? 's' : ''}${h > 0 ? ` ${h}h` : ''}${m > 0 ? ` ${m}min` : ''}`;
    } else if (h > 0) {
      return `${h} heure${h > 1 ? 's' : ''}${m > 0 ? ` ${m}min` : ''}`;
    } else {
      return `${m} minute${m > 1 ? 's' : ''}`;
    }
  };

  // Fonction pour obtenir le nom d'un service par son ID
  const getServiceName = (serviceId: string | undefined) => {
    if (!serviceId) return 'Service non défini';
    const service = services.find(s => s.id === serviceId);
    return service ? service.name : `Service #${serviceId}`;
  };

  useEffect(() => {
    loadWheelData();
  }, [isAuthenticated]);

  const loadWheelData = async () => {
    try {
      console.log('🎡 Début du chargement des données de la roue...');
      
      // Charger les promotions actives et les services en parallèle
      const [promotionsData, servicesData] = await Promise.all([
        promotionApiService.getActivePromotions(),
        serviceApiService.getServices()
      ]);
      
      console.log('📊 Promotions actives récupérées:', promotionsData);
      console.log('📦 Nombre de promotions:', promotionsData?.length || 0);
      
      // Stocker les services
      setServices(Array.isArray(servicesData) ? servicesData : []);
      
      // Si l'utilisateur est authentifié, charger aussi ses promotions actives
      if (isAuthenticated) {
        try {
          const userPromoCodesData = await promotionApiService.getUserPromoCodes();
          console.log('🎁 Codes promo utilisateur:', userPromoCodesData.length);
          setUserActivePromotions(Array.isArray(userPromoCodesData) ? userPromoCodesData : []);
        } catch (error) {
          console.log('Impossible de charger les promotions utilisateur:', error);
          setUserActivePromotions([]);
        }
      }
      
      // Vérifier s'il y a des promotions actives
      if (!promotionsData || promotionsData.length === 0) {
        console.warn('⚠️ Aucune promotion active trouvée');
        setIsActive(false);
        return;
      }
      
      // La roue est active s'il y a des promotions
      setIsActive(true);
      setCanPlay(true);
      setCanWin(true);
      setDaysUntilNextPlay(0);
      setDaysUntilNextWin(0);
      setReplayDelay(7);
      setWinDelay(1);
      
      console.log('✅ Roue activée avec', promotionsData.length, 'promotions');
      
      const colors = ['#FFD700', '#1E90FF', '#FFFFFF', '#FFA500', '#4169E1', '#F0E68C', '#87CEEB', '#FFE4B5'];
      
      // Créer les segments de la roue à partir des promotions actives
      const wheelSegments: WheelSegment[] = promotionsData.map((promo: Promotion, i: number) => ({
        id: `segment-${promo.id}`,
        label: promo.discount_display || promo.name,
        color: colors[i % colors.length],
        rotation: (i * 360) / promotionsData.length,
        promotion: promo
      }));
      
      console.log('✅ Segments créés:', wheelSegments.length);
      setSegments(wheelSegments);
    } catch (error) {
      console.error('❌ Erreur chargement roue:', error);
      setIsActive(false);
    }
  };

  const spinWheel = async () => {
    console.log('🎰 Tentative de tourner la roue...');
    console.log('🔐 Authentifié:', isAuthenticated);
    console.log('🎯 Peut jouer:', canPlay);
    console.log('🎁 Promotions actives:', userActivePromotions.length);
    console.log('📦 Nombre de segments:', segments.length);
    console.log('💫 En rotation:', isSpinning);
    
    if (!isAuthenticated) {
      console.log('⚠️ Utilisateur non authentifié - affichage modal connexion');
      setShowLoginModal(true);
      return;
    }

    if (userActivePromotions.length > 0) {
      console.log('⚠️ L\'utilisateur a déjà des promotions actives');
      alert('Vous avez déjà des promotions actives ! Réclamez-les d\'abord avant de jouer à nouveau.');
      return;
    }

    if (!canPlay) {
      console.log('⚠️ L\'utilisateur ne peut pas encore jouer');
      alert(`Vous devez attendre encore ${formatDelay(daysUntilNextPlay)}.`);
      return;
    }

    if (isSpinning) {
      console.log('⚠️ La roue est déjà en rotation');
      return;
    }
    
    if (segments.length === 0) {
      console.error('❌ Aucun segment disponible - impossible de tourner');
      alert('La roue n\'est pas encore configurée. Veuillez réessayer plus tard.');
      return;
    }

    setIsSpinning(true);
    setShowResult(false);
    
    try {
      console.log('🎲 Sélection aléatoire d\'une promotion...');
      
      // Sélectionner aléatoirement un segment
      const winningIndex = Math.floor(Math.random() * segments.length);
      const winningSegment = segments[winningIndex];
      
      console.log('🎯 Segment gagnant:', winningIndex, winningSegment);
      
      if (winningIndex !== -1) {
        const baseRotation = 360 * 5;
        const segmentAngle = 360 / segments.length;
        const targetAngle = 360 - (winningIndex * segmentAngle + segmentAngle / 2);
        const finalRotation = baseRotation + targetAngle;
        
        setRotation(finalRotation);
        
        setTimeout(async () => {
          setIsSpinning(false);
          setWonPromotion(winningSegment.promotion || null);
          setShowResult(true);
          setCanPlay(false);
          
          // Sauvegarder le code promo gagné dans le backend
          console.log('🔍 Promotion gagnée:', winningSegment.promotion);
          console.log('🔍 Utilisateur authentifié:', isAuthenticated);
          
          if (winningSegment.promotion && winningSegment.promotion.id) {
            console.log('🎯 Tentative de sauvegarde du code promo:', {
              promotionId: winningSegment.promotion.id,
              code: winningSegment.promotion.code,
              name: winningSegment.promotion.name,
              isAuthenticated: isAuthenticated
            });
            
            if (!isAuthenticated) {
              console.log('⚠️ Utilisateur non authentifié - sauvegarde en localStorage uniquement');
              const promoData = {
                code: winningSegment.promotion.code,
                discount: winningSegment.promotion.discount_value || 0,
                discount_type: winningSegment.promotion.discount_type,
                source: 'wheel',
                won_date: new Date().toISOString(),
                promotion_name: winningSegment.promotion.name,
                end_date: winningSegment.promotion.end_date || null
              };
              
              const existingCodes = localStorage.getItem('userPromoCodes');
              let codes = existingCodes ? JSON.parse(existingCodes) : [];
              const codeExists = codes.find((c: any) => c.code === promoData.code);
              if (!codeExists) {
                codes.push(promoData);
                localStorage.setItem('userPromoCodes', JSON.stringify(codes));
                console.log('🎁 Code promo sauvegardé en localStorage (non authentifié):', promoData);
              }
            } else {
              try {
                // 1. Récupérer les informations complètes de la promotion
                const fullPromotion = await promotionApiService.getPromotion(winningSegment.promotion.id);
                console.log('📋 Informations complètes de la promotion:', fullPromotion);
                
                // 2. Activer la promotion si elle n'est pas déjà active
                if (fullPromotion.status !== 'active') {
                  await promotionApiService.updatePromotion(winningSegment.promotion.id, { status: 'active' });
                  console.log('✅ Promotion activée avec succès');
                }
                
                // 3. Sauvegarder le code promo pour l'utilisateur
                const saveResult = await promotionApiService.saveUserPromoCode(winningSegment.promotion.id, 'wheel');
                console.log('✅ Code promo sauvegardé dans le backend avec succès:', saveResult);
                console.log('🎁 Promotion sauvegardée:', fullPromotion.code);
                
                // 4. Vérifier que le code promo est bien dans la liste de l'utilisateur
                const promotionId = winningSegment.promotion?.id;
                const promotionCode = fullPromotion.code;
                if (promotionId) {
                  try {
                    // Attendre un peu pour laisser le temps au backend de se synchroniser
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    
                    const userPromoCodes = await promotionApiService.getUserPromoCodes();
                    console.log('🔍 Vérification détaillée:');
                    console.log('  - ID recherché:', promotionId);
                    console.log('  - Code recherché:', promotionCode);
                    console.log('  - Nombre de codes dans la liste:', userPromoCodes.length);
                    console.log('  - IDs dans la liste:', userPromoCodes.map(p => p.id));
                    console.log('  - Codes dans la liste:', userPromoCodes.map(p => p.code));
                    
                    const isCodeInListById = userPromoCodes.some(promo => promo.id === promotionId);
                    const isCodeInListByCode = userPromoCodes.some(promo => promo.code === promotionCode);
                    
                    if (isCodeInListById || isCodeInListByCode) {
                      console.log('✅ Vérification réussie: Le code promo est bien enregistré dans la liste de l\'utilisateur');
                      console.log(`  - Trouvé par ID: ${isCodeInListById}`);
                      console.log(`  - Trouvé par code: ${isCodeInListByCode}`);
                    } else {
                      console.warn('⚠️ Attention: Le code promo ne semble pas être dans la liste de l\'utilisateur');
                      console.warn('  - Vérification par ID échouée');
                      console.warn('  - Vérification par code échouée');
                    }
                    console.log('📋 Liste complète des codes promo de l\'utilisateur:', userPromoCodes);
                  } catch (verifyError) {
                    console.error('❌ Erreur lors de la vérification de la liste des codes promo:', verifyError);
                  }
                } else {
                  console.warn('⚠️ Impossible de vérifier: ID de promotion manquant');
                }
                
                // Mettre à jour les données de la promotion avec les infos complètes
                setWonPromotion(fullPromotion);
              } catch (error: any) {
                console.error('❌ Erreur lors de la sauvegarde du code promo:', error);
                console.error('📋 Détails de l\'erreur:', error);
                console.error('📋 Status de l\'erreur:', error.response?.status);
                console.error('📋 Data de l\'erreur:', error.response?.data);
                
                // En cas d'erreur backend, sauvegarder temporairement en localStorage comme fallback
                const promoData = {
                  code: winningSegment.promotion.code,
                  discount: winningSegment.promotion.discount_value || 0,
                  discount_type: winningSegment.promotion.discount_type,
                  source: 'wheel',
                  won_date: new Date().toISOString(),
                  promotion_name: winningSegment.promotion.name,
                  end_date: winningSegment.promotion.end_date || null
                };
                
                const existingCodes = localStorage.getItem('userPromoCodes');
                let codes = existingCodes ? JSON.parse(existingCodes) : [];
                const codeExists = codes.find((c: any) => c.code === promoData.code);
                if (!codeExists) {
                  codes.push(promoData);
                  localStorage.setItem('userPromoCodes', JSON.stringify(codes));
                  console.log('🎁 Code promo sauvegardé en fallback localStorage:', promoData);
                }
              }
            }
          }
          
          loadWheelData();
        }, 3000);
      }
    } catch (error: any) {
      setIsSpinning(false);
      alert(error.message || 'Erreur');
    }
  };

  if (!isActive) return null;

  return (
    <section className="py-8 bg-gradient-to-b from-white via-blue-50/20 to-white relative overflow-hidden">
      <div className="absolute top-0 right-1/3 w-96 h-96 bg-gradient-to-br from-blue-100/30 to-yellow-100/30 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-8">
          <motion.div 
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center justify-center space-x-3 mb-2"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 flex items-center justify-center shadow-lg border-2 border-white">
              <span className="text-xl">✨</span>
            </div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 via-blue-700 to-blue-900 bg-clip-text text-transparent">
              Roue de Promo
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-sm text-gray-600"
            dangerouslySetInnerHTML={{ __html: '🎁 Tentez votre chance et gagnez jusqu\'à <span class="text-blue-600 font-bold">50% de réduction</span> sur nos services!' }}
          />
        </div>

        {/* Message pour les promotions actives */}
        {isAuthenticated && userActivePromotions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-xl mx-auto mb-8"
          >
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-2xl p-4 shadow-lg">
              <div className="flex items-center justify-center mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-lg">
                  <span className="text-lg">🎯</span>
                </div>
              </div>
              <h3 className="text-lg font-bold text-center text-gray-800 mb-3">
                {user?.first_name ? `${user.first_name}, vous avez déjà des promotions actives !` : 'Vous avez déjà des promotions actives !'}
              </h3>
              <div className="space-y-2">
                {userActivePromotions.slice(0, 2).map((promo) => (
                  <div key={promo.id} className="bg-white rounded-lg p-3 border border-green-100 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 text-sm truncate">{promo.name}</p>
                        <div className="flex items-center space-x-2">
                          <p className="text-sm text-gray-600">{promo.discount_display}</p>
                          <p className="text-xs text-gray-500 font-mono">{promo.code}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => window.location.href = '/devisrequest'}
                        className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:from-green-600 hover:to-green-700 transition-all shadow-sm hover:shadow-md transform hover:scale-105 ml-2 flex-shrink-0"
                      >
                        Réclamez ici
                      </button>
                    </div>
                  </div>
                ))}
                {userActivePromotions.length > 2 && (
                  <p className="text-center text-xs text-gray-500">
                    +{userActivePromotions.length - 2} autre{userActivePromotions.length - 2 > 1 ? 's' : ''} promotion{userActivePromotions.length - 2 > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-[350px,1fr] gap-8 items-start">
            
            {/* Section Iris à gauche */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="w-full"
            >
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-blue-100 overflow-hidden sticky top-4">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-blue-200">
                  <div className="flex items-center space-x-3">
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-lg">
                        <img 
                          src={avatarUrl} 
                          alt="Iris" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/irisavatar.jpg';
                          }}
                        />
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-base text-gray-900">Iris</h3>
                      <p className="text-xs text-blue-600">Votre assistante</p>
                    </div>
                    <button
                      onClick={() => setShowRules(!showRules)}
                      className="px-3 py-1.5 bg-white rounded-lg text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors shadow-sm flex-shrink-0"
                    >
                      {showRules ? '▲' : '▼ Règles'}
                    </button>
                  </div>
                </div>

                <div className="px-4 py-3">
                  <div className="bg-gradient-to-br from-blue-50 to-yellow-50 rounded-xl p-3 border-l-4 border-blue-500">
                    {!isAuthenticated ? (
                      <div>
                        <p className="text-gray-800 font-semibold text-sm mb-1.5 flex items-center">
                          <span className="mr-2">👋</span> Bienvenue!
                        </p>
                        <p className="text-gray-600 text-xs mb-1.5">
                          Pour participer, connectez-vous.
                        </p>
                        <p className="text-gray-500 text-xs">
                          💡 Pas de compte? Inscrivez-vous!
                        </p>
                      </div>
                    ) : !canPlay ? (
                      <div>
                        <p className="text-gray-800 font-semibold text-sm mb-1.5 flex items-center">
                          <span className="mr-2">⏰</span> Patience!
                        </p>
                        <p className="text-gray-600 text-xs mb-1.5">
                          Revenez dans <span className="font-bold text-blue-600">{formatDelay(daysUntilNextPlay)}</span>
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-800 font-semibold text-sm mb-1.5 flex items-center">
                          <span className="mr-2">🎉</span> C'est parti!
                        </p>
                        <p className="text-gray-600 text-xs mb-2">
                          Cliquez sur le bouton pour tourner:
                        </p>
                        <div className="grid grid-cols-2 gap-1.5 text-xs text-gray-600">
                          <div className="flex items-center space-x-1.5">
                            <span>✨</span>
                            <span>Jusqu'à <span className="font-bold">50%</span></span>
                          </div>
                          <div className="flex items-center space-x-1.5">
                            <span>��</span>
                            <span>Codes <span className="font-bold">exclusifs</span></span>
                          </div>
                          <div className="flex items-center space-x-1.5">
                            <span>🎯</span>
                            <span><span className="font-bold">{segments.length}</span> gains</span>
                          </div>
                          <div className="flex items-center space-x-1.5">
                            <span>⚡</span>
                            <span>Résultat <span className="font-bold">instant</span></span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <AnimatePresence>
                  {showRules && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-2">
                        <div className="bg-gray-50 rounded-xl p-3 space-y-3">
                          <div>
                            <p className="font-semibold text-gray-800 text-sm mb-1.5">📋 Comment jouer?</p>
                            <ul className="space-y-1 ml-5 text-xs text-gray-600">
                              <li>• Connectez-vous</li>
                              <li>• Cliquez sur "TOURNER"</li>
                              <li>• Attendez le résultat</li>
                              <li>• Code promo auto</li>
                              <li>• Rejouez dans {formatDelay(replayDelay)}</li>
                            </ul>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800 text-sm mb-1.5">🎯 Conditions</p>
                            <ul className="space-y-1 ml-5 text-xs text-gray-600">
                              <li>• 1 fois / {formatDelay(replayDelay)}</li>
                              <li>• Selon conditions promo</li>
                              <li>• Non cumulable</li>
                              <li>• Services éligibles</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* La Roue - Design circulaire SVG premium */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className={`flex justify-center items-center ${
                isAuthenticated && userActivePromotions.length > 0 
                  ? 'opacity-40 grayscale pointer-events-none' 
                  : ''
              }`}
            >
              <div className="relative">
                {/* Flèche pointeur vers le bas */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-30">
                  <div className="w-0 h-0 
                    border-l-[18px] border-l-transparent 
                    border-r-[18px] border-r-transparent 
                    border-t-[28px] border-t-yellow-500 
                    drop-shadow-2xl">
                  </div>
                </div>

                <div className="relative p-3 rounded-full bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 shadow-2xl">
                  <div className="relative w-[280px] h-[280px] rounded-full bg-white p-1 shadow-inner">
                    <div 
                      ref={wheelRef}
                      onClick={spinWheel}
                      className={`w-full h-full rounded-full relative shadow-xl ${
                        !isSpinning && isAuthenticated && canPlay && userActivePromotions.length === 0 ? 'cursor-pointer hover:scale-[1.02] transition-transform' : 
                        isSpinning ? 'cursor-wait' : 'cursor-not-allowed opacity-60'
                      }`}
                      style={{ 
                        transform: `rotate(${rotation}deg)`,
                        transition: isSpinning ? 'transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'transform 0.2s ease'
                      }}
                    >
                      <svg className="w-full h-full" viewBox="0 0 100 100">
                        {segments.map((segment, index) => {
                          const angle = 360 / segments.length;
                          const startAngle = index * angle - 90;
                          const endAngle = startAngle + angle;
                          
                          const x1 = 50 + 50 * Math.cos((startAngle * Math.PI) / 180);
                          const y1 = 50 + 50 * Math.sin((startAngle * Math.PI) / 180);
                          const x2 = 50 + 50 * Math.cos((endAngle * Math.PI) / 180);
                          const y2 = 50 + 50 * Math.sin((endAngle * Math.PI) / 180);
                          
                          const largeArcFlag = angle > 180 ? 1 : 0;
                          
                          const textAngle = startAngle + angle / 2;
                          const textRadius = 35;
                          const textX = 50 + textRadius * Math.cos((textAngle * Math.PI) / 180);
                          const textY = 50 + textRadius * Math.sin((textAngle * Math.PI) / 180);
                          
                          // Couleurs claires = texte noir, couleurs foncées = texte blanc
                          const isLightColor = ['#FFD700', '#FFA500', '#2ECC71'].includes(segment.color);
                          const textColor = isLightColor ? '#000000' : '#FFFFFF';
                          const strokeColor = isLightColor ? '#FFFFFF' : '#000000';
                          
                          return (
                            <g key={segment.id}>
                              <path
                                d={`M 50 50 L ${x1} ${y1} A 50 50 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                                fill={segment.color}
                                stroke="#fff"
                                strokeWidth="1"
                              />
                              <text
                                x={textX}
                                y={textY}
                                fontSize="3.5"
                                fontWeight="900"
                                fill={textColor}
                                stroke={strokeColor}
                                strokeWidth="0.8"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                transform={`rotate(${textAngle}, ${textX}, ${textY})`}
                                style={{ 
                                  fontFamily: 'Arial Black, Arial, sans-serif',
                                  paintOrder: 'stroke fill'
                                }}
                              >
                                {segment.label}
                              </text>
                            </g>
                          );
                        })}
                      </svg>

                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-white via-gray-50 to-gray-100 shadow-2xl border-4 border-yellow-400 flex items-center justify-center">
                          {isSpinning ? (
                            <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <span className="text-3xl">🎯</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center mt-5">
                  <button
                    onClick={spinWheel}
                    disabled={isSpinning || !isAuthenticated || !canPlay || (isAuthenticated && userActivePromotions.length > 0)}
                    className={`px-8 py-3 rounded-full font-bold text-white text-base shadow-xl transition-all ${
                      isSpinning || !isAuthenticated || !canPlay || (isAuthenticated && userActivePromotions.length > 0)
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 hover:from-blue-700 hover:via-blue-800 hover:to-blue-900 hover:shadow-2xl hover:scale-105 active:scale-95'
                    }`}
                  >
                    {isSpinning ? '⏳ Tourne...' : 
                     !isAuthenticated ? '🔒 Connexion' : 
                     (isAuthenticated && userActivePromotions.length > 0) ? '🎯 Promotions actives' :
                     !canPlay ? `⏰ ${formatDelay(daysUntilNextPlay)}` : 
                     '🎰 TOURNER LA ROUE'}
                  </button>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </div>

      <AnimatePresence>
        {showResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowResult(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-yellow-200/50 to-blue-200/50 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                {wonPromotion ? (
                  <div className="text-center">
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring" }}
                      className="text-7xl mb-4"
                    >
                      🎉
                    </motion.div>
                    <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-blue-900 bg-clip-text text-transparent">Félicitations!</h3>
                    
                    <div className="bg-gradient-to-br from-blue-50 to-yellow-50 rounded-2xl p-6 mb-6 border-2 border-blue-100">
                      <p className="text-4xl font-bold text-blue-600 mb-3">{wonPromotion.discount_display}</p>
                      <p className="font-semibold text-gray-900 mb-2 text-lg">{wonPromotion.name}</p>
                      <p className="text-sm text-gray-600 mb-4">{wonPromotion.description}</p>
                      
                      {/* Affichage spécial pour les promotions "Achetez X obtenez Y" */}
                      {wonPromotion.discount_type === 'buy_x_get_y' && wonPromotion.buy_service_id && wonPromotion.get_service_id && (
                        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 mb-4 border border-green-200">
                          <p className="text-sm font-semibold text-gray-700 mb-2">🎯 Détails de l'offre :</p>
                          <div className="flex items-center justify-center space-x-2 text-sm">
                            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                              Achetez : {getServiceName(wonPromotion.buy_service_id)}
                            </span>
                            <span className="text-gray-400">→</span>
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium">
                              Obtenez : {getServiceName(wonPromotion.get_service_id)}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="bg-white rounded-xl p-4 border-2 border-dashed border-blue-300 shadow-inner">
                        <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Votre code promo</p>
                        <p className="text-2xl font-mono font-bold text-blue-600 tracking-wider">{wonPromotion.code}</p>
                      </div>
                    </div>
                    
                    
                    <p className="text-xs text-gray-500 mb-6">
                      ✨ {wonPromotion.end_date ? 
                        `Valable jusqu'au ${new Date(wonPromotion.end_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}` : 
                        'Promotion sans date d\'expiration'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring" }}
                      className="text-7xl mb-4"
                    >
                      😊
                    </motion.div>
                    <h3 className="text-3xl font-bold mb-4 text-gray-800">Pas cette fois!</h3>
                    <p className="text-gray-600 mb-6">Retentez votre chance dans {formatDelay(replayDelay)}</p>
                  </div>
                )}
                
                <button
                  onClick={() => {
                    setShowResult(false);
                    setWonPromotion(null);
                  }}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-900 shadow-lg hover:shadow-xl transition-all"
                >
                  Fermer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de connexion pour les utilisateurs non connectés */}
      <AnimatePresence>
        {showLoginModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowLoginModal(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/50 to-yellow-200/50 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <div className="text-center">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className="text-7xl mb-4"
                  >
                    🔒
                  </motion.div>
                  <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-blue-900 bg-clip-text text-transparent">
                    Connexion requise
                  </h3>
                  
                  <div className="bg-gradient-to-br from-blue-50 to-yellow-50 rounded-2xl p-6 mb-6 border-2 border-blue-100">
                    <p className="text-gray-700 text-lg mb-4">
                      Vous devez vous connecter pour jouer à la roue de promo !
                    </p>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p className="flex items-center justify-center gap-2">
                        <span>✨</span>
                        <span>Gagnez jusqu'à 50% de réduction</span>
                      </p>
                      <p className="flex items-center justify-center gap-2">
                        <span>🎯</span>
                        <span>Codes promo exclusifs</span>
                      </p>
                      <p className="flex items-center justify-center gap-2">
                        <span>🎁</span>
                        <span>Offres spéciales et cadeaux</span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        setShowLoginModal(false);
                        navigate('/login');
                      }}
                      className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-900 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                    >
                      Se connecter
                    </button>
                    
                    <button
                      onClick={() => setShowLoginModal(false)}
                      className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium text-base hover:bg-gray-200 transition-all"
                    >
                      Peut-être plus tard
                    </button>
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-4">
                    Pas encore de compte ?{' '}
                    <button
                      onClick={() => {
                        setShowLoginModal(false);
                        navigate('/register');
                      }}
                      className="text-blue-600 font-semibold hover:text-blue-700 underline"
                    >
                      Inscrivez-vous gratuitement
                    </button>
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PromoWheel;
